/**
 * Build Excel-style groups (Agg System + Year with inline subtotals)
 *
 * Groups by agg_system_ndx AND report_year, so each system has separate
 * rows for each year. Production values are summed from filtered wells.
 * Permits use system-level values (once per system+year).
 */

import { computePctPermit, compareAggSystems } from "../utils";
import { createEmptyTotals, accumulateWellDetailRow } from "./buildTotals";

/**
 * Build Excel-style grouped data
 * Groups by agg_system_ndx + report_year
 * @param {import("../types").ProductionRow[]} rows - Production rows
 * @returns {{groups: import("../types").ExcelGroup[], grandTotals: import("../types").Totals & {pct_permit: number}, years: number[]}}
 */
export const buildExcelGroups = (rows) => {
  if (!rows || rows.length === 0) {
    return { groups: [], grandTotals: createEmptyTotals(), years: [] };
  }

  // Group by agg_system_ndx + year
  const groupMap = new Map();
  const grandTotals = createEmptyTotals();
  const grandSeenSystems = new Set();
  const yearsSet = new Set();

  for (const row of rows) {
    const aggKey = row.agg_system_ndx;
    const aggName = row.agg_system_name || "Not Aggregated";
    const year = row.report_year;
    const groupKey = `${aggKey ?? "null"}-${year}`;

    yearsSet.add(year);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        agg_system_ndx: aggKey,
        agg_system_name: aggName,
        report_year: year,
        // Start with empty totals - we'll accumulate filtered wells' production
        // and add system permits once
        totals: createEmptyTotals(),
        seenSystems: new Set(),
        wells: [],
      });
    }

    const group = groupMap.get(groupKey);

    // Add the well row (with per-well values) to the group
    group.wells.push(row);

    // Accumulate filtered wells' production, but only count permits once per system
    // This uses the same logic as DetailTable for consistency
    accumulateWellDetailRow(group.totals, row, group.seenSystems);

    // Grand totals: same logic - sum filtered wells' production, permits once per system
    accumulateWellDetailRow(grandTotals, row, grandSeenSystems);
  }

  // Get sorted years (descending - most recent first)
  const years = Array.from(yearsSet).sort((a, b) => b - a);

  const groups = Array.from(groupMap.values())
    .map((g) => ({
      agg_system_ndx: g.agg_system_ndx,
      agg_system_name: g.agg_system_name,
      report_year: g.report_year,
      totals: g.totals || createEmptyTotals(),
      pct_permit: computePctPermit(g.totals || createEmptyTotals()),
      wells: g.wells.sort((a, b) =>
        (a.cuwcd_well_number || "").localeCompare(b.cuwcd_well_number || "")
      ),
    }))
    .sort((a, b) => {
      // Sort by agg system name first (with "Not Aggregated" at the bottom)
      const nameCompare = compareAggSystems(
        a.agg_system_name,
        b.agg_system_name
      );
      if (nameCompare !== 0) return nameCompare;
      // Then by year descending (most recent first)
      return b.report_year - a.report_year;
    });

  return {
    groups,
    grandTotals: {
      ...grandTotals,
      pct_permit: computePctPermit(grandTotals),
    },
    years,
  };
};
